import type { TJobDialogMetaData, TJobMetaData } from "@/api/models/patient/patient";
import type {
  ETreatmentPlanJobAutomaticSignal,
  ETreatmentPlanJobManualSignal,
  ETreatmentPlanJobRecurringSignal,
  ETreatmentPlanJobState,
  ETreatmentPlanJobSystemSignal,
  ETreatmentPlanJobUiControlType,
} from "@/api/models/treatmentPlan/job";
import { useI18n } from "vue-i18n";

/** Per-state metadata fields that hold a plain translatable string. */
export type TJobLabelKey = "jobTitle" | "errorMessage" | "successMessage" | "nextInstruction";

/**
 * Minimal shape a job needs to expose so its labels can be resolved.
 * Kept loose on purpose so this works for `TTreatmentPlanJob`,
 * `TJobsAvailableForUser`, flow graph nodes, etc.
 */
export interface TJobLabelSource {
  name: string;
  state: ETreatmentPlanJobState;
  meta_data?: TJobMetaData | null;
  signal_system_type?: ETreatmentPlanJobSystemSignal | null;
  signal_manual_type?: ETreatmentPlanJobManualSignal | null;
  signal_automatic_type?: ETreatmentPlanJobAutomaticSignal | null;
  signal_recurring_type?: ETreatmentPlanJobRecurringSignal | null;
  signal_ui_control_type?: ETreatmentPlanJobUiControlType | null;
}

export interface TResolveLabelOptions {
  /** Which per-state metadata field to read. Defaults to `"jobTitle"`. */
  labelKey?: TJobLabelKey;
  /** Resolve the label for a different state than `job.state`. */
  state?: ETreatmentPlanJobState;
  /**
   * Explicit i18n key to try as the second tier. When omitted, the default
   * `models.treatmentPlan.job.signals.{signalKey}.{state}` key is used.
   */
  i18nKey?: string;
  /**
   * Whether to attempt the i18n tier at all. Set to `false` for fields that
   * are stored verbatim (e.g. `nextInstruction`) and never translated.
   * Defaults to `true`.
   */
  translate?: boolean;
  /** Final fallback when neither metadata nor a translation exists. */
  fallback?: string;
}

export const JOB_SIGNAL_I18N_PREFIX = "models.treatmentPlan.job.signals";

/**
 * The two i18n primitives the resolvers depend on. In app code these come
 * from `useI18n()`; in tests they can be built over the raw locale object so
 * the exact same resolution logic runs without a Vue/i18n runtime.
 */
export interface TJobLabelI18n {
  /** Translate a key (only ever called when `te(key)` is true). */
  t: (key: string) => string;
  /** Whether a translation key exists. */
  te: (key: string) => boolean;
}

/**
 * Framework-agnostic core. Resolves job display strings with a consistent,
 * metadata-first fallback:
 *   1. metadata label   — `meta_data.labels.en[state][labelKey]`
 *   2. i18n translation — when the key exists (`te` guard before `t`)
 *   3. fallback         — `job.name` by default
 *
 * Designed to be malleable: every tier can be overridden per call, the i18n
 * tier can be skipped, and any per-state label field (jobTitle, errorMessage,
 * successMessage, nextInstruction) flows through the same path.
 *
 * Use {@link useTreatmentPlanJobLabels} inside components; call this directly
 * (e.g. from Playwright helpers) when no `useI18n()` context is available.
 */
export const createJobLabelResolvers = ({ t, te }: TJobLabelI18n) => {
  /** Signal identity used to build the default i18n key. */
  const getSignalKey = (job: TJobLabelSource): string =>
    job.signal_system_type ||
    job.signal_manual_type ||
    job.signal_automatic_type ||
    job.signal_recurring_type ||
    job.signal_ui_control_type ||
    (job.name ? job.name.toLocaleLowerCase().replaceAll(" ", "_") : "");

  /** Safely narrow `meta_data` to an object (it can be a non-object/`null`). */
  const getMetaData = (job: TJobLabelSource): TJobMetaData | undefined =>
    typeof job.meta_data === "object" && job.meta_data !== null
      ? (job.meta_data as TJobMetaData)
      : undefined;

  /** Read a single per-state metadata label block (`labels.en[state]`). */
  const getStateLabels = (job: TJobLabelSource, state: ETreatmentPlanJobState = job.state) =>
    getMetaData(job)?.labels?.en?.[state];

  /** Raw metadata string for a per-state field (the first resolution tier). */
  const getMetaLabel = (
    job: TJobLabelSource,
    labelKey: TJobLabelKey = "jobTitle",
    state: ETreatmentPlanJobState = job.state
  ): string | undefined => getStateLabels(job, state)?.[labelKey];

  const buildSignalKey = (
    job: TJobLabelSource,
    suffix: ETreatmentPlanJobState | string = job.state
  ): string => `${JOB_SIGNAL_I18N_PREFIX}.${getSignalKey(job)}.${suffix}`;

  /**
   * The signal-translation tier on its own: `te(key) ? t(key) : fallback`.
   * Use for labels that are translation-only with no metadata override
   * (e.g. a job's "return" action label).
   */
  const translateSignal = (
    job: TJobLabelSource,
    options: { suffix?: ETreatmentPlanJobState | string; i18nKey?: string; fallback?: string } = {}
  ): string => {
    const { suffix = job.state, i18nKey, fallback = job.name } = options;
    const key = i18nKey ?? buildSignalKey(job, suffix);
    return te(key) ? t(key) : fallback;
  };

  /** Generic 3-tier resolver — the core the helpers below build on. */
  const resolveLabel = (job: TJobLabelSource, options: TResolveLabelOptions = {}): string => {
    const {
      labelKey = "jobTitle",
      state = job.state,
      i18nKey,
      translate = true,
      fallback = job.name,
    } = options;

    const metaValue = getMetaLabel(job, labelKey, state);
    if (metaValue) return metaValue;

    if (translate) {
      const key = i18nKey ?? buildSignalKey(job, state);
      if (key && te(key)) return t(key);
    }

    return fallback;
  };

  /** The job's display title (matches the original `_.text` logic). */
  const resolveText = (job: TJobLabelSource): string => resolveLabel(job, { labelKey: "jobTitle" });

  /** Per-state dialog metadata (title/subtitle/accept/cancel), or `{}`. */
  const resolveDialog = (
    job: TJobLabelSource,
    state: ETreatmentPlanJobState = job.state
  ): TJobDialogMetaData => getStateLabels(job, state)?.dialog ?? {};

  return {
    resolveLabel,
    resolveText,
    resolveDialog,
    translateSignal,
    getSignalKey,
    getMetaData,
    getMetaLabel,
    getStateLabels,
  };
};

export type TJobLabelResolvers = ReturnType<typeof createJobLabelResolvers>;

/** Component-facing composable: wires the resolvers to the active `useI18n()`. */
export const useTreatmentPlanJobLabels = (): TJobLabelResolvers => {
  const { t, te } = useI18n();
  return createJobLabelResolvers({ t, te });
};
